import React, { useState, useRef, useEffect } from 'react';
import styles from './XptFileUpload.module.css';
import PageHeader from '../pageheader/Pageheader.tsx';
import Modal from '../modal/Modal.tsx';
import JSZip from 'jszip';
import { useLocation, useNavigate } from 'react-router-dom';
import { uploadFile, getFileDetails, saveXptFile, deleteFile } from '../../api/api';
import { useUser } from '../../contexts/UserContext.tsx';
import { useToast } from '../toast/ToastProvider.tsx';
import Loader from '../loader/Loader.tsx';

interface XPTFile {
    id: string;
    name: string;
    size: number;
    type: string;
    standard: string;
    file: File;
}

interface ZipGroup {
    id: string;
    zipName: string;
    zipSize: number;
    xptFiles: XPTFile[];
    zipFile: File;
}

interface VariableMapping {
    id: string;
    dataset: string;
    variable: string;
}

interface SupportingFile {
    studyFileId: number;
    fileName: string;
    fileType: string;
    fileSize: string;
    status: string;
    cdiscStandardVersion: string;
    defineVersion?: string;
    parentStudyFileId?: number | null;
    isEditingVersion?: boolean;
}

interface FileDetailsResponse {
    supportingFiles: SupportingFile[];
}

// Standard options for upload dropdown
const uploadStandardOptions = [
    { value: 'SDTMIG v3.1.2', label: 'SDTMIG v3.1.2' },
    { value: 'SDTMIG v3.1.3', label: 'SDTMIG v3.1.3' },
    { value: 'SDTMIG v3.2', label: 'SDTMIG v3.2' },
    { value: 'SDTMIG v3.3', label: 'SDTMIG v3.3' },
    { value: 'SDTMIG v3.4', label: 'SDTMIG v3.4' },
    { value: 'SDTMIG-AP v1.0', label: 'SDTMIG-AP v1.0' },
    { value: 'SDTMIG-MD v1.0', label: 'SDTMIG-MD v1.0' },
    { value: 'SDTMIG-MD v1.1', label: 'SDTMIG-MD v1.1' },
];

const getVersionOptions = (files: SupportingFile[]) => {
    return uploadStandardOptions;
};

// UploadedFilesList component
const UploadedFilesList: React.FC<{ 
    files: SupportingFile[], 
    onFileUpdate: (fileId: number, field: string, value: string) => void,
    onFileDelete: (fileId: number) => void,
    isLoadingFiles?: boolean
}> = ({ files, onFileUpdate, onFileDelete, isLoadingFiles = false }) => {
    const versionOptions = getVersionOptions(files);

    const getFileIcon = (fileType: string) => {
        if (fileType === 'ZIP') {
            return (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                        d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"
                        fill="#FFA500"
                    />
                    <path d="M14 2v6h6" stroke="#FF8C00" strokeWidth="2" />
                    <text x="12" y="16" fontSize="6" fill="white" textAnchor="middle" fontWeight="bold">
                        ZIP
                    </text>
                </svg>
            );
        }

        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                    d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"
                    fill="#4d9fae"
                />
                <path d="M14 2v6h6" stroke="#2d8292" strokeWidth="2" />
                <text x="12" y="16" fontSize="7" fill="white" textAnchor="middle" fontWeight="bold">
                    XPT
                </text>
            </svg>
        );
    };

    if (isLoadingFiles) {
        return (
            <div className={styles.uploadedFilesContainer}>
                <div className={styles.uploadedFilesHeader}>
                    <h3 className={styles.uploadedFilesTitle}>
                        Uploaded Files [0]
                    </h3>
                </div>
                <div className={styles.uploadedFilesEmpty}>
                    <Loader text="Loading files..." size="sm" />
                </div>
            </div>
        );
    }

    if (files.length === 0) {
        return (
            <div className={styles.uploadedFilesContainer}>
                <div className={styles.uploadedFilesHeader}>
                    <h3 className={styles.uploadedFilesTitle}>
                        Uploaded Files [0]
                    </h3>
                </div>
                <div className={styles.uploadedFilesEmpty}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                        <path
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <p>No files uploaded yet</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.uploadedFilesContainer}>
            <div className={styles.uploadedFilesHeader}>
                <h3 className={styles.uploadedFilesTitle}>
                    Uploaded Files [{files.filter((file) => !file.parentStudyFileId).length}]
                </h3>
            </div>
            
            <div className={styles.uploadedFilesScroll}>
                {files.filter((file) => !file.parentStudyFileId).map((file) => (
                    <div key={file.studyFileId} className={styles.uploadedFileItem}>
                        <div className={styles.uploadedFileIcon}>
                            {getFileIcon(file.fileType)}
                        </div>
                        
                        <div className={styles.uploadedFileInfo}>
                            <div className={styles.uploadedFileName}>
                                {file.fileName}
                            </div>
                            {/* <div className={styles.uploadedFileDetails}>
                                <span>ID: {file.studyFileId}</span>
                                <span>•</span>
                                <span className={styles.versionText}>
                                    {file.cdiscStandardVersion || 'Set version'}
                                </span>
                                <span>•</span>
                                <span>{file.status}</span>
                                {file.parentStudyFileId && (
                                    <>
                                        <span>•</span>
                                        <span>Parent: {file.parentStudyFileId}</span>
                                    </>
                                )}
                            </div> */}
                        </div>
                        
                        <div className={`${styles.uploadedFileBadge} ${styles[file.fileType.toLowerCase()]}`}>
                            {file.fileType}
                        </div>
                        
                        <button
                            className={styles.deleteButton}
                            onClick={() => onFileDelete(file.studyFileId)}
                            title="Delete file"
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="10" stroke="#E53E3E" strokeWidth="2" />
                                <path
                                    d="M15 9l-6 6m0-6l6 6"
                                    stroke="#E53E3E"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};

type FileToDelete = {
    type: 'individual';
    file: XPTFile;
} | {
    type: 'zip-child';
    zipId: string;
    zipName: string;
    file: XPTFile;
} | {
    type: 'zip';
    zipGroup: ZipGroup;
};

const XPTFileUpload: React.FC = () => {
    const [individualFiles, setIndividualFiles] = useState<XPTFile[]>([]);
    const [zipGroups, setZipGroups] = useState<ZipGroup[]>([]);
    const [isDragging, setIsDragging] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const searchParams = new URLSearchParams(location.search);
    const studyId = searchParams.get('studyId');
    const { user } = useUser();
    const showToast = useToast();
    const [globalVariables, setGlobalVariables] = useState({
        global: '',
        dataset: '',
        variable: ''
    });
    const [mappings, setMappings] = useState<VariableMapping[]>([]);
    const [fileToDelete, setFileToDelete] = useState<FileToDelete | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [uploadedFileToDelete, setUploadedFileToDelete] = useState<SupportingFile | null>(null);
    const [isFileListModalOpen, setIsFileListModalOpen] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState<SupportingFile[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isLoadingFiles, setIsLoadingFiles] = useState(true);
    const [isExtractingFiles, setIsExtractingFiles] = useState(false);
     const [editingFileId, setEditingFileId] = useState<number | null>(null);
    const dropdownRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

    // Fetch uploaded files function
    const fetchUploadedFiles = async () => {
        if (studyId) {
            try {
                setIsLoadingFiles(true);
                const response = await getFileDetails(studyId);
                setUploadedFiles(response.data.supportingFiles || []);
            } catch (error) {
                console.error('Error fetching uploaded files:', error);
            } finally {
                setIsLoadingFiles(false);
            }
        }
    };

    // Fetch uploaded files on component mount
    useEffect(() => {
        fetchUploadedFiles();
    }, [studyId]);

    // Close dropdown when clicking outside
 useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (editingFileId !== null) {
                const dropdownElement = dropdownRefs.current[editingFileId];
                if (dropdownElement && !dropdownElement.contains(event.target as Node)) {
                    setEditingFileId(null);
                }
            }
        };

        // Add listener with a small delay to avoid closing immediately on open
        if (editingFileId !== null) {
            // Use setTimeout to add the listener after the current event loop
            const timeoutId = setTimeout(() => {
                document.addEventListener('mousedown', handleClickOutside);
            }, 0);

            return () => {
                clearTimeout(timeoutId);
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [editingFileId]);

    const versionOptions = getVersionOptions(uploadedFiles);

    const hasExtractedFiles = uploadedFiles.length > 0;

    const handleFileListModalClose = () => {
        setIsFileListModalOpen(false);
    };

    // Extract XPT files from ZIP
    const extractZipFiles = async (zipFile: File): Promise<XPTFile[]> => {
        try {
            const zip = new JSZip();
            const contents = await zip.loadAsync(zipFile);
            const xptFiles: XPTFile[] = [];

            for (const [filename, file] of Object.entries(contents.files)) {
                if (!file.dir && filename.toLowerCase().endsWith('.xpt')) {
                    const blob = await file.async('blob');
                    const extractedFile = new File([blob], filename, { type: 'application/octet-stream' });
                    
                    xptFiles.push({
                        id: `${zipFile.name}-${filename}-${Date.now()}-${Math.random()}`,
                        name: filename,
                        size: blob.size,
                        type: 'application/octet-stream',
                        standard: 'SDTMIG v3.4',
                        file: extractedFile
                    });
                }
            }

            return xptFiles;
        } catch (error) {
            console.error('Error extracting ZIP:', error);
            return [];
        }
    };

    // Handle file selection
    const handleFileSelect = async (selectedFiles: FileList | null) => {
        if (!selectedFiles) return;

        setIsProcessing(true);
        setIsExtractingFiles(true);

        const filesArray = Array.from(selectedFiles);
        const newIndividualFiles: XPTFile[] = [];
        const newZipGroups: ZipGroup[] = [];

        for (const file of filesArray) {
            if (file.name.toLowerCase().endsWith('.zip')) {
                const xptFiles = await extractZipFiles(file);
                if (xptFiles.length > 0) {
                    newZipGroups.push({
                        id: `zip-${file.name}-${Date.now()}`,
                        zipName: file.name,
                        zipSize: file.size,
                        xptFiles,
                        zipFile: file
                    });
                }
            } else if (file.name.toLowerCase().endsWith('.xpt')) {
                newIndividualFiles.push({
                    id: `${file.name}-${Date.now()}`,
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    standard: 'SDTMIG v3.4',
                    file
                });
            }
        }

        setIndividualFiles(prev => [...prev, ...newIndividualFiles]);
        setZipGroups(prev => [...prev, ...newZipGroups]);
        if (newIndividualFiles.length > 0 || newZipGroups.length > 0) {
            setIsFileListModalOpen(true);
        }
        setIsProcessing(false);
        setIsExtractingFiles(false);
    };

    // Handle drag and drop
    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        handleFileSelect(e.dataTransfer.files);
    };

    // Handle uploaded file deletion
    const handleUploadedFileDelete = (fileId: number) => {
        const file = uploadedFiles.find(f => f.studyFileId === fileId);
        if (file) {
            setUploadedFileToDelete(file);
            setIsDeleteModalOpen(true);
        }
    };

    const handleBack = () => {
        navigate(-1);
    };

    const handleCancel = () => {
        navigate("/defineui/assigned-studies", { replace: true });
    };

    // Confirm uploaded file deletion
    const handleConfirmUploadedFileDelete = async () => {
        if (!uploadedFileToDelete || !user?.userId) return;

        try {
            await deleteFile(uploadedFileToDelete.studyFileId, user.userId);
            
            setUploadedFiles(prev => {
                const filteredFiles = prev.filter(f => f.studyFileId !== uploadedFileToDelete.studyFileId);
                
                if ((uploadedFileToDelete.fileType || '').toUpperCase() === 'ZIP') {
                    return filteredFiles.filter(f => f.parentStudyFileId !== uploadedFileToDelete.studyFileId);
                }
                
                return filteredFiles;
            });
            
            showToast.success('File deleted successfully');
            setIsDeleteModalOpen(false);
            setUploadedFileToDelete(null);
        } catch (error) {
            console.error('Error deleting file:', error);
            showToast.error('Failed to delete file');
        }
    };

    // Delete handlers
    const handleDeleteClick = (deleteItem: FileToDelete) => {
        setFileToDelete(deleteItem);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = () => {
        if (!fileToDelete) return;

        if (fileToDelete.type === 'individual') {
            setIndividualFiles(prev => prev.filter(f => f.id !== fileToDelete.file.id));
        } else if (fileToDelete.type === 'zip-child') {
            setZipGroups(prev => prev.filter(group => group.id !== fileToDelete.zipId));
        } else if (fileToDelete.type === 'zip') {
            setZipGroups(prev => prev.filter(group => group.id !== fileToDelete.zipGroup.id));
        }

        setIsDeleteModalOpen(false);
        setFileToDelete(null);
    };

    const handleCancelDelete = () => {
        setIsDeleteModalOpen(false);
        setFileToDelete(null);
        setUploadedFileToDelete(null);
    };

    // Update file standard
    const handleStandardChange = (fileId: string, standard: string, zipId?: string) => {
        if (zipId) {
            setZipGroups(prev =>
                prev.map(group =>
                    group.id === zipId
                        ? {
                            ...group,
                            xptFiles: group.xptFiles.map(f =>
                                f.id === fileId ? { ...f, standard } : f
                            )
                        }
                        : group
                )
            );
        } else {
            setIndividualFiles(prev =>
                prev.map(f => (f.id === fileId ? { ...f, standard } : f))
            );
        }
    };

    // Format file size
    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    };

    // Get file icon
    const getFileIcon = (filename: string, isZip: boolean = false, compact: boolean = false) => {
        const size = compact ? 20 : 32;
        if (isZip) {
            return (
                <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
                    <path
                        d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"
                        fill="#FFA500"
                    />
                    <path d="M14 2v6h6" stroke="#FF8C00" strokeWidth="2" />
                    <text x="12" y="16" fontSize={compact ? 4 : 7} fill="white" textAnchor="middle" fontWeight="bold">
                        ZIP
                    </text>
                </svg>
            );
        }

        return (
            <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
                <path
                    d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"
                    fill="#4d9fae"
                />
                <path d="M14 2v6h6" stroke="#2d8292" strokeWidth="2" />
                <text x="12" y="16" fontSize={compact ? 5 : 8} fill="white" textAnchor="middle" fontWeight="bold">
                    XPT
                </text>
            </svg>
        );
    };

     const handleToggleDropdown = (fileId: number, event: React.MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
        setEditingFileId(editingFileId === fileId ? null : fileId);
    };

    // Handle file updates (for version editing)
   const handleFileUpdate = (fileId: number, newVersion: string) => {
        setUploadedFiles(prev => prev.map(file => 
            file.studyFileId === fileId 
                ? { ...file, cdiscStandardVersion: newVersion }
                : file
        ));
        setHasUnsavedChanges(true);
        setEditingFileId(null);
    };

    // Handle form submission
    const handleSave = async () => {
        if (!user?.userId || !studyId) {
            showToast.error('User ID or Study ID is missing');
            return;
        }

        try {
            setIsSaving(true);
            
            const saveData = {
                supportingFiles: uploadedFiles.map(file => ({
                    studyFileId: file.studyFileId,
                    fileName: file.fileName,
                    fileType: file.fileType,
                    status: file.status,
                    cdiscStandardVersion: file.cdiscStandardVersion,
                    parentStudyFileId: file.parentStudyFileId
                }))
            };
            
            await saveXptFile(user.userId, studyId, saveData);
            showToast.success('Version updated successfully.');
            setHasUnsavedChanges(false);
            
        } catch (err: any) {
            console.error('Error saving data:', err);
            showToast.error('Failed to save data. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleContinue = () => {
        // Get the defineVersion from the uploaded files (assuming it's in the response or file details)
        const defineVersion = uploadedFiles.length > 0 ? uploadedFiles[0].defineVersion || "-1" : "-1";
        navigate('/defineui/value-level-variables', { state: { studyId, defineVersion } });
    };

    // Handle upload to endpoint
    const handleUploadToEndpoint = async () => {
        try {
            setIsProcessing(true);

            const allFiles: File[] = [];
            
            individualFiles.forEach(xptFile => {
                allFiles.push(xptFile.file);
            });
            
            zipGroups.forEach(zipGroup => {
                allFiles.push(zipGroup.zipFile);
            });

            const formData = new FormData();
            allFiles.forEach(file => {
                formData.append('documents', file);
            });

            if (!user?.userId) {
                throw new Error('User ID is required for upload');
            }
            
            await uploadFile(studyId, user.userId, formData);
            setIsFileListModalOpen(false);
            showToast.success('File uploaded successfully');
            await fetchUploadedFiles();
            handleReset();

        } catch (err) {
            console.error('Upload error:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to upload files. Please try again.';
            showToast.error(errorMessage);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleReset = () => {
        setIndividualFiles([]);
        setZipGroups([]);
        setGlobalVariables({ global: '', dataset: '', variable: '' });
        setMappings([]);
    };

    const getTotalFileCount = () => {
        const individualCount = individualFiles.length;
        const zipFilesCount = zipGroups.reduce((sum, group) => sum + group.xptFiles.length, 0);
        return individualCount + zipFilesCount;
    };

    const userPermissions = {
        canCreateStudy: true,
        canViewDefineFiles: true,
        canUploadDocuments: true,
        canViewDefinePreview: true,
        canAccessIssueManagement: true,
        canViewProgressCharts: true,
    };

    // Render delete modal content based on delete type
    const renderDeleteModalContent = () => {
        if (uploadedFileToDelete) {
            const isZipFile = (uploadedFileToDelete.fileType || '').toUpperCase() === 'ZIP';
            const isZipChild = uploadedFileToDelete.parentStudyFileId != null;
            
            if (isZipChild) {
                const parentZip = uploadedFiles.find(f => f.studyFileId === uploadedFileToDelete.parentStudyFileId);
                const parentZipName = parentZip?.fileName || 'Unknown ZIP';
                
                return (
                    <>
                        <div className={styles.deleteWarningIcon}>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="10" stroke="#E53E3E" strokeWidth="2" />
                                <path d="M12 8v4M12 16h.01" stroke="#E53E3E" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                        </div>
                        <h3 className={styles.deleteModalTitle}>Cannot Delete Individual File from ZIP</h3>
                        <p className={styles.deleteModalMessage}>
                            The file <strong>{uploadedFileToDelete.fileName}</strong> is part of the ZIP archive <strong>{parentZipName}</strong>.
                        </p>
                        <p className={styles.deleteModalWarning}>
                            Individual files within a ZIP cannot be deleted separately. The entire ZIP archive will be removed.
                        </p>
                    </>
                );
            } else if (isZipFile) {
                const childCount = uploadedFiles.filter(f => f.parentStudyFileId === uploadedFileToDelete.studyFileId).length;
                
                return (
                    <>
                        <div className={styles.deleteWarningIcon}>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="10" stroke="#E53E3E" strokeWidth="2" />
                                <path d="M12 8v4M12 16h.01" stroke="#E53E3E" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                        </div>
                        <h3 className={styles.deleteModalTitle}>Are you sure you want to delete this ZIP archive?</h3>
                        <p className={styles.deleteModalMessage}>
                            {uploadedFileToDelete.fileName} ({childCount} files)
                        </p>
                        <p className={styles.deleteModalWarning}>
                            All files within this ZIP archive will be removed. This action cannot be undone.
                        </p>
                    </>
                );
            } else {
                return (
                    <>
                        <div className={styles.deleteWarningIcon}>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="10" stroke="#E53E3E" strokeWidth="2" />
                                <path d="M12 8v4M12 16h.01" stroke="#E53E3E" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                        </div>
                        <h3 className={styles.deleteModalTitle}>Are you sure you want to delete this file?</h3>
                        <p className={styles.deleteModalMessage}>{uploadedFileToDelete.fileName}</p>
                        <p className={styles.deleteModalWarning}>This action cannot be undone.</p>
                    </>
                );
            }
        }

        if (!fileToDelete) return null;

        if (fileToDelete.type === 'individual') {
            return (
                <>
                    <div className={styles.deleteWarningIcon}>
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="10" stroke="#E53E3E" strokeWidth="2" />
                            <path d="M12 8v4M12 16h.01" stroke="#E53E3E" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    </div>
                    <h3 className={styles.deleteModalTitle}>Are you sure you want to delete this file?</h3>
                    <p className={styles.deleteModalMessage}>{fileToDelete.file.name}</p>
                    <p className={styles.deleteModalWarning}>This action cannot be undone.</p>
                </>
            );
        } else if (fileToDelete.type === 'zip-child') {
            return (
                <>
                    <div className={styles.deleteWarningIcon}>
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="10" stroke="#E53E3E" strokeWidth="2" />
                            <path d="M12 8v4M12 16h.01" stroke="#E53E3E" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    </div>
                    <h3 className={styles.deleteModalTitle}>Cannot Delete Individual File from ZIP</h3>
                    <p className={styles.deleteModalMessage}>
                        The file <strong>{fileToDelete.file.name}</strong> is part of the ZIP archive <strong>{fileToDelete.zipName}</strong>.
                    </p>
                    <p className={styles.deleteModalWarning}>
                        Individual files within a ZIP cannot be deleted separately. The entire ZIP archive will be removed.
                    </p>
                </>
            );
        } else if (fileToDelete.type === 'zip') {
            return (
                <>
                    <div className={styles.deleteWarningIcon}>
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="10" stroke="#E53E3E" strokeWidth="2" />
                            <path d="M12 8v4M12 16h.01" stroke="#E53E3E" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    </div>
                    <h3 className={styles.deleteModalTitle}>Are you sure you want to delete this ZIP archive?</h3>
                    <p className={styles.deleteModalMessage}>
                        {fileToDelete.zipGroup.zipName} ({fileToDelete.zipGroup.xptFiles.length} files)
                    </p>
                    <p className={styles.deleteModalWarning}>
                        All files within this ZIP archive will be removed. This action cannot be undone.
                    </p>
                </>
            );
        }
    };

    return (
        <>
            <PageHeader
                title="Import XPT files"
                showRefresh={false}
                showCreateStudy={true}
                showDefineFiles={true}
                showUploadDocuments={true}
                showDefinePreview={true}
                showIssueManagement={true}
                showProgressCharts={true}
                selectedStudyId={0}
                permissions={userPermissions}
                onCreateStudy={() => console.log('Create new study')}
                onDefineFiles={() => console.log('View define files')}
                onUploadDocuments={() => console.log('Upload documents')}
                onDefinePreview={() => console.log('View define preview')}
                onIssueManagement={() => console.log('Issue management')}
                onProgressCharts={() => console.log('Progress charts')}
            />
            <div className={styles.container}>
                <div className={styles.content}>
                    {/* Left Panel - File Upload */}
                    <div className={styles.leftPanel}>
                        {/* Drag and Drop Area */}
                        <div
                            className={`${styles.dropzone} ${isDragging ? styles.dropzoneDragging : ''} ${isProcessing ? styles.dropzoneProcessing : ''}`}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={() => !isProcessing && fileInputRef.current?.click()}
                        >
                            <div className={styles.dropzoneIcon}>
                                {isProcessing ? (
                                    <div className={styles.spinner}>
                                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" opacity="0.25" />
                                            <path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                                <animateTransform
                                                    attributeName="transform"
                                                    type="rotate"
                                                    from="0 12 12"
                                                    to="360 12 12"
                                                    dur="1s"
                                                    repeatCount="indefinite"
                                                />
                                            </path>
                                        </svg>
                                    </div>
                                ) : (
                                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                                        <path
                                            d="M7 18C4.79086 18 3 16.2091 3 14C3 11.7909 4.79086 10 7 10C7.22253 10 7.44084 10.0162 7.65411 10.0473C8.15536 6.61806 11.1705 4 14.8 4C18.7764 4 22 7.22355 22 11.2C22 13.9105 20.5119 16.2618 18.2667 17.3284"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                        />
                                        <path
                                            d="M12 13L12 21M12 13L15 16M12 13L9 16"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                )}
                            </div>
                            <p className={styles.dropzoneText}>
                                {isProcessing ? 'Processing files...' : 'Drag and Drop files here'}
                            </p>
                            {!isProcessing && (
                                <>
                                    <p className={styles.dropzoneSubtext}>or</p>
                                    <button className={styles.selectButton}>Select files</button>
                                    <p className={styles.dropzoneHint}>Supported: .xpt and .zip files</p>
                                </>
                            )}
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                accept=".xpt,.zip"
                                onChange={e => handleFileSelect(e.target.files)}
                                className={styles.fileInput}
                                disabled={isProcessing}
                            />
                        </div>

                        {/* Uploaded Files List */}
                        <UploadedFilesList 
                            files={uploadedFiles} 
                            onFileUpdate={handleFileUpdate} 
                            onFileDelete={handleUploadedFileDelete}
                            isLoadingFiles={isLoadingFiles}
                        />
                    </div>

                    {/* Right Panel - Extracted Files */}
                    <div className={styles.rightPanel}>
                        {/* Extracted Files List */}
                        <div className={styles.fileList}>
                            <div className={styles.fileListHeader}>
                                <h3 className={styles.fileListTitle}>Extracted Files</h3>
                            </div>

                          
                            {hasExtractedFiles && (
                                <div className={styles.fileItems}>
                                    {isExtractingFiles ? (
                                        <div className={styles.fileItem} style={{ justifyContent: 'center', padding: '20px' }}>
                                            <Loader text="Extracting files..." size="sm" />
                                        </div>
                                    ) : (
                                        uploadedFiles
                                            .filter((file): file is SupportingFile => 
                                                'fileType' in file && file.fileType === 'XPT'
                                            ).map(file => (
                                                <div 
                                                    key={file.studyFileId} 
                                                    className={styles.fileItem}
                                                    ref={(el) => {
                                                        dropdownRefs.current[file.studyFileId] = el;
                                                    }}
                                                >
                                                    <div className={styles.fileIcon}>{getFileIcon(file.fileName, false, true)}</div>

                                                    <div className={styles.fileInfo}>
                                                        <div className={styles.fileName}>{file.fileName}</div>
                                                        <div className={styles.fileSize}>{file.fileSize || ''}</div>
                                                    </div>

                                                    <div className={styles.fileStandardContainer}>
                                                        <button
                                                            onClick={(e) => handleToggleDropdown(file.studyFileId, e)}
                                                            className={styles.versionButton}
                                                            type="button"
                                                        >
                                                            <span>{file.cdiscStandardVersion || 'Set version'}</span>
                                                            <svg 
                                                                width="16" 
                                                                height="16" 
                                                                viewBox="0 0 24 24" 
                                                                fill="none" 
                                                                className={`${styles.dropdownArrow} ${editingFileId === file.studyFileId ? styles.dropdownArrowOpen : ''}`}
                                                            >
                                                                <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                            </svg>
                                                        </button>

                                                        {editingFileId === file.studyFileId && (
                                                            <div className={styles.customDropdown}>
                                                                {versionOptions.map((opt: { value: string; label: string }) => (
                                                                    <div
                                                                        key={opt.value}
                                                                        className={`${styles.dropdownOption} ${file.cdiscStandardVersion === opt.value ? styles.selected : ''}`}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleFileUpdate(file.studyFileId, opt.value);
                                                                        }}
                                                                    >
                                                                        <span>{opt.label}</span>
                                                                        {file.cdiscStandardVersion === opt.value && (
                                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                                                                <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                            </svg>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Save Button */}
                        <button
                            className={styles.generateButton}
                            onClick={handleSave}
                            disabled={!hasUnsavedChanges || isSaving || uploadedFiles.length === 0}
                        >
                            {isSaving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </div>

                {/* Footer Actions */}
                <div className={styles.footer}>
                    <button className={styles.resetButton} onClick={handleContinue}>
                        Continue
                    </button>
                    {/* <button className={styles.resetButton} onClick={handleReset}>
                        Reset
                    </button> */}
                    <button className={styles.cancelButton} onClick={handleCancel}>Cancel</button>
                    <button className={styles.cancelButton} onClick={handleBack}>Back</button>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            <div className={styles.deleteModalWrapper}>
                <Modal isOpen={isDeleteModalOpen} onClose={handleCancelDelete} title="Delete File">
                    <div className={styles.deleteModalContent}>
                        {renderDeleteModalContent()}

                        <div className={styles.deleteModalActions}>
                            <button className={styles.deleteModalButton} onClick={uploadedFileToDelete ? handleConfirmUploadedFileDelete : handleConfirmDelete}>
                                Delete
                            </button>
                            <button className={styles.cancelModalButton} onClick={handleCancelDelete}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </Modal>
            </div>

            {/* File List Modal */}
            <div className={styles.fileListModalWrapper}>
                <Modal isOpen={isFileListModalOpen} onClose={handleFileListModalClose} title="Files to Upload">
                    <div className={styles.fileListModalContent}>
                        {individualFiles.length === 0 && zipGroups.length === 0 ? (
                            <div className={styles.emptyState}>
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                    <path
                                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <p>No files uploaded yet</p>
                            </div>
                        ) : (
                            <div className={styles.simpleFileList}>
                                {individualFiles.map(file => (
                                    <div key={file.id} className={styles.simpleFileItem}>
                                        <div className={styles.fileIcon}>{getFileIcon(file.name)}</div>
                                        <div className={styles.fileInfo}>
                                            <div className={styles.fileName}>{file.name}</div>
                                            <div className={styles.fileSize}>{formatFileSize(file.size)}</div>
                                        </div>
                                        <button
                                            className={styles.removeButton}
                                            onClick={() => handleDeleteClick({ type: 'individual', file })}
                                            title="Remove file"
                                        >
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                                                <path
                                                    d="M15 9l-6 6m0-6l6 6"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                />
                                            </svg>
                                        </button>
                                    </div>
                                ))}

                                {zipGroups.map(zipGroup => (
                                    <div key={zipGroup.id} className={styles.simpleFileItem}>
                                        <div className={styles.fileIcon}>{getFileIcon(zipGroup.zipName, true)}</div>
                                        <div className={styles.fileInfo}>
                                            <div className={styles.fileName}>
                                                {zipGroup.zipName}
                                                {/* <span className={styles.fileCount}>
                                                    ({zipGroup.xptFiles.length} files)
                                                </span> */}
                                            </div>
                                            <div className={styles.fileSize}>{formatFileSize(zipGroup.zipSize)}</div>
                                        </div>
                                        <button
                                            className={styles.removeButton}
                                            onClick={() => handleDeleteClick({ type: 'zip', zipGroup })}
                                            title="Remove ZIP archive"
                                        >
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                                                <path
                                                    d="M15 9l-6 6m0-6l6 6"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                />
                                            </svg>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {(individualFiles.length > 0 || zipGroups.length > 0) && (
                            <div className={styles.uploadToEndpoint}>
                                <button
                                    className={styles.uploadEndpointButton}
                                    onClick={handleUploadToEndpoint}
                                    disabled={isProcessing}
                                >
                                    {isProcessing ? (
                                        <div className={styles.buttonLoader}></div>
                                    ) : (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                            <path
                                                d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    )}
                                    {isProcessing ? 'Uploading...' : 'Upload File to Server'}
                                </button>
                                <p className={styles.uploadHint}>
                                    All extracted XPT files will be uploaded
                                </p>
                            </div>
                        )}
                    </div>
                </Modal>
            </div>
        </>
    );
};

export default XPTFileUpload;